import type { Permission } from '@saniplus/contracts';
import {
  Building2,
  Calendar,
  ChartColumn,
  CircleCheckBig,
  Clock,
  LayoutGrid,
  Settings2,
  ShieldCheck,
  TriangleAlert,
  Users,
  type LucideIcon,
} from '@saniplus/ui';

/**
 * The admin navigation, as data.
 *
 * The shape, the labels and the permission of every row come from
 * `design/pages/admin/A1-dashboard/JUDGEMENT.md` §5.5, which is binding on the
 * whole portal: an item the caller lacks permission for is **not rendered**,
 * never disabled. A greyed "Paramètres" tells a supervisor the route exists and
 * that they are not trusted with it, which contradicts a backend whose
 * out-of-scope reads answer 404 rather than 403.
 *
 * `labelKey` is a key in the `nav` catalogue, never a string: this module is
 * imported by a component the `react/jsx-no-literals` rule guards.
 */
export interface NavItemDef {
  readonly key: string;
  /** Absent on a pure group header. */
  readonly href?: string;
  readonly labelKey: string;
  readonly icon?: LucideIcon;
  /** Every one of these is required. An empty list is "always visible". */
  readonly permissions: readonly Permission[];
  readonly children?: readonly NavItemDef[];
}

interface NavGroupDef {
  readonly key: string;
  readonly items: readonly NavItemDef[];
  /** Draws the 1 px rule above the elevated-permission block (JUDGEMENT §5.2). */
  readonly separated?: boolean;
}

export const NAV_GROUPS: readonly NavGroupDef[] = [
  {
    key: 'operations',
    items: [
      {
        key: 'dashboard',
        href: '/dashboard',
        labelKey: 'items.dashboard',
        icon: LayoutGrid,
        // The two endpoints the screen reads have exactly these requirements.
        permissions: ['punch:read', 'exception:read'],
      },
      {
        key: 'horaires',
        labelKey: 'items.horaires',
        icon: Calendar,
        permissions: ['shift:read'],
        children: [
          {
            key: 'horaires-calendrier',
            href: '/horaires/calendrier',
            labelKey: 'items.horairesCalendrier',
            permissions: ['shift:read'],
          },
          {
            key: 'horaires-series',
            href: '/horaires/series',
            labelKey: 'items.horairesSeries',
            permissions: ['series:read'],
          },
          {
            key: 'horaires-ouverts',
            href: '/horaires/ouverts',
            labelKey: 'items.horairesOuverts',
            permissions: ['shift:read'],
          },
        ],
      },
      {
        key: 'pointages',
        labelKey: 'items.pointages',
        icon: Clock,
        permissions: ['punch:read'],
        children: [
          {
            key: 'pointages-en-cours',
            href: '/pointages/en-cours',
            labelKey: 'items.pointagesEnCours',
            permissions: ['punch:read'],
          },
          {
            key: 'pointages-historique',
            href: '/pointages',
            labelKey: 'items.pointagesHistorique',
            permissions: ['punch:read'],
          },
        ],
      },
      {
        key: 'taches',
        href: '/taches',
        labelKey: 'items.taches',
        icon: CircleCheckBig,
        permissions: ['task:read'],
      },
      {
        key: 'heures',
        labelKey: 'items.heures',
        icon: ChartColumn,
        permissions: ['timesheet:read'],
        children: [
          {
            key: 'heures-feuilles',
            href: '/heures/feuilles',
            labelKey: 'items.heuresFeuilles',
            permissions: ['timesheet:read'],
          },
          {
            key: 'heures-exports',
            href: '/heures/exports',
            labelKey: 'items.heuresExports',
            permissions: ['timesheet:read', 'timesheet:export'],
          },
        ],
      },
      {
        key: 'exceptions',
        href: '/exceptions',
        labelKey: 'items.exceptions',
        icon: TriangleAlert,
        permissions: ['exception:read'],
      },
      {
        key: 'organisation',
        labelKey: 'items.organisation',
        icon: Building2,
        permissions: [],
        children: [
          {
            key: 'organisation-contrats',
            href: '/organisation/contrats',
            labelKey: 'items.organisationContrats',
            permissions: ['contract:read'],
          },
          {
            key: 'organisation-sites',
            href: '/organisation/sites',
            labelKey: 'items.organisationSites',
            permissions: ['site:read'],
          },
          /*
           * There is no `organisation-zones` row. A18 puts zones INLINE on the
           * site they belong to — a zone name without its plant is not
           * something anyone looks up, and the mid-shift zone change (D-03) is
           * a fact about the site. A nav item leading to a page that only ever
           * said "choose a site first" is a promise the product does not keep.
           */
        ],
      },
      {
        key: 'personnel',
        labelKey: 'items.personnel',
        icon: Users,
        permissions: ['user:read'],
        children: [
          {
            key: 'personnel-employes',
            href: '/personnel/employes',
            labelKey: 'items.personnelEmployes',
            permissions: ['user:read'],
          },
        ],
      },
    ],
  },
  {
    key: 'elevated',
    separated: true,
    items: [
      {
        key: 'audit',
        href: '/audit',
        labelKey: 'items.audit',
        icon: ShieldCheck,
        permissions: ['audit:read'],
      },
      {
        key: 'parametres',
        href: '/parametres',
        labelKey: 'items.parametres',
        icon: Settings2,
        permissions: ['settings:read'],
      },
    ],
  },
];

/** Every navigable href in the tree. */
export function navHrefs(groups: readonly NavGroupDef[] = NAV_GROUPS): string[] {
  const out: string[] = [];
  const walk = (items: readonly NavItemDef[]): void => {
    for (const item of items) {
      if (item.href !== undefined) out.push(item.href);
      if (item.children !== undefined) walk(item.children);
    }
  };
  walk(groups.flatMap((group) => [...group.items]));
  return out;
}

/**
 * The ONE href that is current for a pathname: the longest href that is a path
 * prefix of it.
 *
 * JUDGEMENT §5.3: `aria-current="page"` is carried by exactly one element, the
 * deepest match; a parent whose child is active gets none, because being
 * expanded is its signal. Prefix matching is what keeps `/pointages/:id`
 * marking *Historique* rather than nothing at all.
 */
export function currentHref(
  pathname: string,
  groups: readonly NavGroupDef[] = NAV_GROUPS,
): string | null {
  let best: string | null = null;
  for (const href of navHrefs(groups)) {
    if (pathname !== href && !pathname.startsWith(`${href}/`)) continue;
    if (best === null || href.length > best.length) best = href;
  }
  return best;
}

/**
 * Drop everything the caller may not see, then drop the parents and groups
 * that are left empty (§5.5: "a group whose every child is hidden hides the
 * group; the divider collapses when both elevated items are hidden").
 */
export function visibleGroups(
  held: readonly Permission[],
  groups: readonly NavGroupDef[] = NAV_GROUPS,
): NavGroupDef[] {
  const allowed = (item: NavItemDef): boolean =>
    item.permissions.every((permission) => held.includes(permission));

  return groups
    .map((group) => {
      const items = group.items
        .map((item) => {
          if (!allowed(item)) return null;
          if (item.children === undefined) return item;
          const children = item.children.filter(allowed);
          if (children.length === 0) return null;
          return { ...item, children };
        })
        .filter((item): item is NavItemDef => item !== null);
      return { ...group, items };
    })
    .filter((group) => group.items.length > 0);
}

/**
 * The screen an account with NO portal permission lands on (Phase 4, Q6).
 *
 * Not a nav item — it is what a signed-in worker sees instead of an empty
 * navigation and a `Forbidden` dashboard — and outside the shell, because a
 * shell with nothing in it is the problem the screen explains.
 */
export const NO_PORTAL_ACCESS_PATH = '/sans-acces';

/**
 * Where a caller lands after signing in.
 *
 * §5.5: "if `Tableau de bord` itself is hidden, the first visible item becomes
 * the post-login landing route — the router must not assume `/dashboard`
 * exists for every caller." And when nothing at all is visible, the answer is
 * the no-portal-access screen rather than a dashboard that would refuse them.
 */
export function landingPath(held: readonly Permission[]): string {
  for (const group of visibleGroups(held)) {
    for (const item of group.items) {
      if (item.href !== undefined) return item.href;
      const child = item.children?.find((candidate) => candidate.href !== undefined);
      if (child?.href !== undefined) return child.href;
    }
  }
  return NO_PORTAL_ACCESS_PATH;
}
